import * as React from 'react';
import { IconButton } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';
import clsx from 'clsx';

type PaginationProps = {
  totalRows: number;
  pageNo: number;
  rowsPerPage: number;
  showSmallerValues?: boolean;
  setRowsPerPage: (rows: number) => void;
  setPageNo: (pageNo: number) => void;
  isESComponent?: boolean;
  perPageHeader?: string;
  additionalClasses?: string;
};

export default function Pagination({
  totalRows,
  pageNo,
  rowsPerPage,
  showSmallerValues,
  setRowsPerPage,
  setPageNo,
  isESComponent,
  perPageHeader = 'Rows per page',
  additionalClasses,
}: PaginationProps) {
  const [inputValue, setInputValue] = React.useState(pageNo.toString());

  React.useEffect(() => {
    setInputValue(pageNo.toString());
  }, [pageNo]);

  const maxESLimit = isESComponent ? 10000 : Infinity;
  const maxPage = rowsPerPage === -1 ? 1 : Math.ceil(totalRows / rowsPerPage);
  const maxDirectNavigationAllowed =
    rowsPerPage === -1
      ? 1
      : maxESLimit % rowsPerPage === 0 || !isESComponent
      ? Math.ceil(Math.min(maxESLimit, totalRows) / rowsPerPage)
      : Math.floor(Math.min(maxESLimit, totalRows) / rowsPerPage);

  const handleRowsPerPageChange: React.ChangeEventHandler<HTMLSelectElement> = (
    event
  ) => {
    const nextRowsPerPage = Number(event.target.value);

    const nextPage =
      rowsPerPage === -1 || nextRowsPerPage === -1
        ? 1
        : Math.floor(((pageNo - 1) * rowsPerPage) / nextRowsPerPage) + 1;

    setRowsPerPage(nextRowsPerPage);
    setPageNo(nextPage);
  };

  const handleSubmit = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>
      | React.FocusEvent<HTMLInputElement>
  ) => {
    const prevPage = pageNo;
    const newPage = e.currentTarget.value ? Number(e.currentTarget.value) : 1;
    if (newPage > maxDirectNavigationAllowed) {
      setInputValue(prevPage.toString());
      return;
    }
    const _page = Math.min(Math.max(newPage, 1), maxPage);
    setInputValue(_page.toString());
    setPageNo(_page);
  };

  const gotoPrevPage = () => {
    if (pageNo === 1) return;
    const prevPage = pageNo - 1;
    setPageNo(prevPage);
  };

  const gotoNextPage = () => {
    if (pageNo === maxPage) return;
    const nextPage = pageNo + 1;
    setPageNo(nextPage);
  };

  return (
    <div className={clsx("flex h-12 items-center text-xs", additionalClasses)}>
      <span>{perPageHeader}: </span>

      <select
        value={rowsPerPage}
        onChange={handleRowsPerPageChange}
        className="border-0 mr-5"
      >
        {showSmallerValues && <option value={20}>20</option>}
        <option value={50}>50</option>
        <option value={100}>100</option>
        {!showSmallerValues && <option value={2000}>2000</option>}
      </select>

      <IconButton onClick={gotoPrevPage} disabled={pageNo === 1}>
        <KeyboardArrowLeft className="text-lg" />
      </IconButton>

      <IconButton onClick={gotoNextPage} disabled={pageNo === maxPage}>
        <KeyboardArrowRight className="text-lg" />
      </IconButton>

      <span className="ml-4">Page</span>

      <input
        className="mx-1 w-12 rounded border border-solid border-gray-200 p-1"
        type="number"
        value={inputValue}
        min={1}
        max={maxDirectNavigationAllowed}
        onChange={(e) => {
          setInputValue(e.target.value);
        }}
        onKeyUp={(e) => {
          if (e.key === 'Enter') {
            handleSubmit(e);
          }
        }}
        onBlur={handleSubmit}
      />

      <span>of {maxPage}</span>
    </div>
  );
}
